import type { Pesquisa } from '@eleg360/types';

/**
 * Comparação A/B entre pesquisas e a guarda de compatibilidade metodológica.
 *
 * O protótipo (proto:991) só compara quando `a.met===b.met && a.abr===b.abr` e,
 * quando não compara, escreve "Recortes não comparáveis". A ideia está certa: um
 * delta entre duas pesquisas de recortes diferentes é um número correto com o
 * rótulo errado — parece comparação e não é.
 *
 * O QUE O MODELO DE DADOS PERMITE CONFERIR (eleg.pesquisa / `Pesquisa` em
 * packages/types): instituto, periodo, metodologia, amostra, abrangencia,
 * cenario, registro_tse, confianca, origem, resultados, created_at.
 *
 * Disso, três campos sustentam um julgamento de compatibilidade e viram guarda
 * REAL, que bloqueia:
 *   - `abrangencia` — recorte territorial (município × estado × bairro);
 *   - `metodologia` — como a coleta foi feita (presencial, telefone, online);
 *   - `cenario`   — espontânea × estimulada, e qual cenário estimulado.
 *
 * E há dimensões que a guarda NÃO consegue verificar, com a causa raiz de cada
 * uma. Elas são declaradas ao usuário em vez de silenciadas — a checagem é
 * parcial e quem lê precisa saber disso:
 *   - MARGEM DE ERRO: não existe campo. `Pesquisa` não declara margem, nem
 *     nível de confiança estatístico (`confianca` aqui é a leitura subjetiva da
 *     equipe: 'alta'|'media'|'baixa'). Sem isso, não dá para dizer se uma
 *     variação de 3,5 p.p. está dentro ou fora da margem.
 *   - INTERVALO ENTRE OS LEVANTAMENTOS: `periodo` é `varchar(100)` de texto
 *     livre ('10 a 12 de agosto de 2026'), não data. `created_at` é a data do
 *     cadastro, não a do campo. O sistema não sabe quantos dias separam A de B,
 *     nem sequer qual das duas é mais antiga.
 *   - REPRESENTATIVIDADE DA AMOSTRA: `amostra` existe e é informada, mas não há
 *     critério cadastrado de amostra mínima nem de plano amostral, então os dois
 *     n são exibidos para conferência humana em vez de virarem um veto inventado.
 */

export type DimensaoCompat = {
  rotulo: string;
  a: string;
  b: string;
  compativel: boolean;
};

export type Compatibilidade = {
  comparavel: boolean;
  /** Todas as dimensões efetivamente conferidas, compatíveis ou não. */
  dimensoes: DimensaoCompat[];
  /** Subconjunto de `dimensoes` que divergiu — é o que a guarda nomeia. */
  incompativeis: DimensaoCompat[];
  /** Dimensões que o cadastro não permite conferir, com a causa raiz. */
  naoVerificaveis: string[];
};

export type LinhaComparativo = {
  nome: string;
  /** `null` = a pesquisa não perguntou esse item. Ausência não é zero. */
  va: number | null;
  vb: number | null;
  /** `null` quando qualquer um dos lados não perguntou. */
  delta: number | null;
};

const normalizar = (v: string | undefined | null) =>
  (v ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const n = (v: number) => v.toLocaleString('pt-BR');

export function avaliarCompatibilidade(a: Pesquisa, b: Pesquisa): Compatibilidade {
  const naoInformado = 'não informado';
  const par = (rotulo: string, va?: string, vb?: string): DimensaoCompat => ({
    rotulo,
    a: va?.trim() || naoInformado,
    b: vb?.trim() || naoInformado,
    // Campo vazio nos dois lados não é prova de igualdade: nada foi conferido,
    // então não pode contar como "compatível".
    compativel: !!normalizar(va) && normalizar(va) === normalizar(vb),
  });

  const dimensoes = [
    par('Abrangência', a.abrangencia, b.abrangencia),
    par('Metodologia', a.metodologia, b.metodologia),
    par('Cenário', a.cenario, b.cenario),
  ];

  const incompativeis = dimensoes.filter((d) => !d.compativel);

  const naoVerificaveis = [
    'Margem de erro: não existe campo de margem de erro no cadastro de pesquisa, então o sistema não sabe se a variação cabe dentro da margem. Confira no relatório do instituto.',
    `Intervalo entre os levantamentos: "período" é texto livre ("${a.periodo}" e "${b.periodo}"), não data; o sistema não calcula quantos dias separam as duas, nem qual veio antes.`,
    `Representatividade da amostra: não há critério cadastrado de amostra mínima. A: n=${n(a.amostra ?? 0)}; B: n=${n(b.amostra ?? 0)}.`,
  ];

  return {
    comparavel: incompativeis.length === 0,
    dimensoes,
    incompativeis,
    naoVerificaveis,
  };
}

export function compararResultados(a: Pesquisa, b: Pesquisa): LinhaComparativo[] {
  const ra = a.resultados ?? {};
  const rb = b.resultados ?? {};
  const nomes = [...new Set([...Object.keys(ra), ...Object.keys(rb)])];

  return nomes.map((nome) => {
    const va = typeof ra[nome] === 'number' ? ra[nome] : null;
    const vb = typeof rb[nome] === 'number' ? rb[nome] : null;
    // Arredondamento a 1 casa evita o 3.4999999999999996 do ponto flutuante
    // sem mexer na precisão que as pesquisas realmente têm (décimo de p.p.).
    const delta = va === null || vb === null ? null : Math.round((vb - va) * 10) / 10;
    return { nome, va, vb, delta };
  });
}

/** Texto em que a busca livre procura — espelha `pqTexto` do protótipo (proto:1769). */
export function textoBuscavel(p: Pesquisa): string {
  return normalizar(
    [
      p.instituto,
      p.periodo,
      p.metodologia,
      String(p.amostra ?? ''),
      p.abrangencia,
      p.cenario,
      p.confianca,
      p.registro_tse,
      p.origem === 'ia_jornal' ? 'imprensa' : 'gabinete',
      ...Object.keys(p.resultados ?? {}),
    ]
      .filter(Boolean)
      .join(' '),
  );
}

/**
 * Filtro local: todos os termos precisam aparecer (E, não OU), sem acento e sem
 * distinção de caixa. É local porque `GET /eleg/pesquisas` não aceita filtro de
 * texto — a tela busca dentro do que já baixou, igual ao protótipo.
 */
export function filtrarPesquisas(pesquisas: Pesquisa[], consulta: string): Pesquisa[] {
  const termos = normalizar(consulta).split(' ').filter(Boolean);
  if (termos.length === 0) return pesquisas;
  return pesquisas.filter((p) => {
    const texto = textoBuscavel(p);
    return termos.every((t) => texto.includes(t));
  });
}

/**
 * Atalhos de busca derivados do dado carregado — instituto, abrangência e
 * cenário que realmente existem nas pesquisas cadastradas, com a contagem.
 *
 * O protótipo oferece uma lista fixa ('Ceará', 'Datafolha', 'Senado', 'O Povo',
 * proto:1775) que casa com o mock dele e prometeria, aqui, buscas que não
 * retornam nada. Derivar do dado mantém a promessa do chip verdadeira: todo
 * atalho oferecido traz pelo menos uma linha, e o número ao lado já diz quantas.
 */
export function atalhosDeBusca(pesquisas: Pesquisa[]): Array<{ termo: string; n: number }> {
  const contagem = new Map<string, number>();
  for (const p of pesquisas) {
    for (const bruto of [p.instituto, p.abrangencia, p.cenario]) {
      const termo = bruto?.trim();
      if (!termo) continue;
      contagem.set(termo, (contagem.get(termo) ?? 0) + 1);
    }
  }
  return [...contagem.entries()]
    .map(([termo, n]) => ({ termo, n }))
    .sort((x, y) => y.n - x.n || x.termo.localeCompare(y.termo, 'pt-BR'));
}
